import { Action, createReducer, on } from '@ngrx/store';
import { Status } from '../../../models/status';
import {
  getAllBagsAndAncillaryFees,
  getAllBagsAndAncillaryFeesComplete,
  initializeFeeState,
  loadFeeResults,
  loadFeeResultsComplete,
  setGetBagsAndAncillaryFeesStatus,
  setSelectedBagAndAncillaryFeePassenger,
  getFeesWorkflowSdcFeeResults,
  getFeesWorkflowSdcFeeResultsComplete,
  setFeesWorkflowSelectedFee,
  setFeesWorkflowSelectedSegments,
  setFeesWorkflowSelectedPassengers,
  getDynamicFees,
  getDynamicFeesComplete,
  getDynamicFeesFailed,
  setPassengerPetFeeTypes,
  clearPassengerPetFeeTypes,
} from './fee.actions';
import { feeAdapter, initialFeeState, INTERNATIONAL_RESTRICTION_ERROR, PassengerOverrideType } from './fee.state';
import { NameNumberUtil } from '../../../utils/name-number-util';
import { hasNoPetFees } from '../../../utils/pet-fee-helper';

const featureReducer = createReducer(
  initialFeeState,
  on(initializeFeeState, (state) => ({ ...state, ...initialFeeState })),
  on(loadFeeResults, (state) => ({
    ...state,
    feeStatus: Status.UPDATING,
  })),
  on(loadFeeResultsComplete, (state, { response }) => ({
    ...state,
    feeResults: feeAdapter.setOne(response, state.feeResults),
    feeStatus: Status.STABLE,
    feeError: response.error,
  })),
  on(getAllBagsAndAncillaryFees, (state) => ({
    ...state,
    bagsAndAncillaryFeesStatus: Status.UPDATING,
    bagsAndAncillaryFeesError: null,
    bagsAndAncillaryFees: null,
    selectedBagAndAncillaryFeePassenger: null,
  })),
  on(getAllBagsAndAncillaryFeesComplete, (state, { response }) => ({
    ...state,
    bagsAndAncillaryFees: response,
    bagsAndAncillaryFeesStatus: Status.STABLE,
    bagsAndAncillaryFeesError: response.error,
    selectedBagAndAncillaryFeePassenger: response?.response?.feesByPassengerRS?.passengers[0],
  })),
  on(setSelectedBagAndAncillaryFeePassenger, (state, { selectedPassengerNameNumber }) => ({
    ...state,
    selectedBagAndAncillaryFeePassenger:
      state.bagsAndAncillaryFees?.response?.feesByPassengerRS?.passengers?.filter(
        (passenger) => passenger.paxNameNumber === selectedPassengerNameNumber
      )[0] ?? null,
  })),
  on(setGetBagsAndAncillaryFeesStatus, (state, { status }) => ({
    ...state,
    bagsAndAncillaryFeesStatus: status,
  })),
  on(getFeesWorkflowSdcFeeResults, (state) => ({
    ...state,
    feesWorkflowSdcFeeStatus: Status.UPDATING,
  })),
  on(getFeesWorkflowSdcFeeResultsComplete, (state, { response }) => ({
    ...state,
    feesWorkflowSdcFeeResults: feeAdapter.setOne(response, state.feeResults),
    feesWorkflowSdcFeeStatus: Status.STABLE,
    feesWorkflowSdcFeeError: response.error,
  })),
  on(setFeesWorkflowSelectedFee, (state, { selectedFee }) => ({
    ...state,
    feesWorkflowSelectedFee: selectedFee,
  })),
  on(setFeesWorkflowSelectedSegments, (state, { selectedSegments }) => ({
    ...state,
    feesWorkflowSelectedSegments: selectedSegments,
  })),
  on(setFeesWorkflowSelectedPassengers, (state, { selectedPassengers }) => ({
    ...state,
    feesWorkflowSelectedPassengers: selectedPassengers,
  })),
  on(getDynamicFees, (state) => ({
    ...state,
    dynamicFeeResults: null,
    dynamicFeeStatus: Status.UPDATING,
    dynamicFeeError: null,
  })),
  on(getDynamicFeesComplete, (state, { response, requestPassengers }) => {
    const paxOverrideTypes: PassengerOverrideType = {};

    requestPassengers?.forEach((passenger) => {
      paxOverrideTypes[passenger.nameNumber] = passenger.overrideType;
    });

    // Only selected guests without pet fees should block pricing with the international restriction (see hasNoPetFees)
    const selectedPaxIds = Object.keys(state.selectedPaxPetFeeTypes);
    const missingPetFees =
      response?.feeDetail?.passengers?.some(
        (passenger) =>
          hasNoPetFees(passenger) && selectedPaxIds.some((paxId) => NameNumberUtil.IsEquivalent(passenger.paxNameNumber, paxId))
      ) ?? false;

    let dynamicFeeError = response?.error || null;
    if (missingPetFees) {
      dynamicFeeError = INTERNATIONAL_RESTRICTION_ERROR;
    }

    return {
      ...state,
      dynamicFeeResults: response?.feeDetail || null,
      dynamicFeeStatus: Status.STABLE,
      dynamicFeeError,
      selectedPaxPricedOverrideTypes: paxOverrideTypes,
    };
  }),
  on(getDynamicFeesFailed, (state, { error }) => ({
    ...state,
    dynamicFeeResults: null,
    dynamicFeeStatus: Status.FAILED,
    dynamicFeeError: error,
  })),
  on(setPassengerPetFeeTypes, (state, { passengerNameNumber, petFeeTypes, overrideType }) => ({
    ...state,
    selectedPaxPetFeeTypes: {
      ...state.selectedPaxPetFeeTypes,
      [passengerNameNumber]: {
        feeTypes: petFeeTypes,
        overrideType,
      },
    },
  })),
  on(clearPassengerPetFeeTypes, (state, { passengerNameNumber }) => {
    // Get destructured object without the key of the passengerNameNumber to be removed
    const { [passengerNameNumber]: removed, ...remainingState } = state.selectedPaxPetFeeTypes;
    return {
      ...state,
      selectedPaxPetFeeTypes: remainingState,
    };
  })
);

export function feeServiceReducer(state = initialFeeState, action: Action) {
  return featureReducer(state, action);
}
